<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field class="mb-2">
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>

          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.news_type') }}
              </span>
            </p>
            <b-select v-model="filterType" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="A">{{ $t('attributes.news_link_type') }}</option>
              <option value="B">{{ $t('attributes.news_normal_type') }}</option>
              <option value="B1">{{ $t('attributes.news_blog_type') }}</option>
            </b-select>
          </b-field>

          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.news_catalog') }}
              </span>
            </p>
            <b-select v-model="filterCatalog" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="近期活動">近期活動</option>
              <option value="活動回顧">活動回顧</option>
            </b-select>
          </b-field>

          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.news_dates') }}
              </span>
            </p>
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedNewsDates"
              range
              @input="onSelectedNewsDates"
            >
            </b-datepicker>
          </b-field>

          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.news') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      :row-class="setRowClass"
      style="max-width: 1400px; margin:0 auto;"
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="picture"
        :label="$t('attributes.picture')"
        width="100"
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.picture"
          :href="NewsImage(props.row.picture)"
          @click.prevent="onClickImage(props.row.picture)"
        >
          <figure class="image">
            <img
              :src="NewsImage(props.row.picture)"
              :alt="$t('attributes.picture')"
              style="width: 80px; margin: auto;"
            />
          </figure>
        </a>
        <p v-else class="has-text-danger has-text-weight-medium">無</p>
      </b-table-column>

      <b-table-column
        field="type"
        :label="$t('attributes.news_type')"
        width="100"
        sortable
        v-slot="props"
      >
        {{ newsType(props.row.type) }}
      </b-table-column>

      <b-table-column
        field="catalog"
        :label="$t('attributes.news_catalog')"
        width="100"
        sortable
        v-slot="props"
      >
        {{ props.row.catalog }}
      </b-table-column>

      <b-table-column
        field="topic"
        :label="$t('attributes.topic')"
        sortable
        v-slot="props"
      >
        <a v-if="props.row.type === 'A'" :href="props.row.url" target="_blank">
          {{ props.row.topic }}
        </a>
        <a
          v-else-if="props.row.type === 'B'"
          :href="newsUrl(props.row)"
          target="_blank"
        >
          {{ props.row.topic }}
        </a>
        <a
          v-else-if="props.row.type === 'B1'"
          :href="newsBlogUrl(props.row)"
          target="_blank"
        >
          {{ props.row.topic }}
        </a>
      </b-table-column>

      <b-table-column
        field="create_time"
        :label="$t('attributes.create_date')"
        width="180"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.create_time) }}
      </b-table-column>

      <b-table-column
        field="start_date"
        :label="$t('attributes.news_dates')"
        width="220"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDates([props.row.start_date, props.row.end_date]) }}
      </b-table-column>

      <b-table-column :width="170" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <a
            :href="newsPreviewUrl(props.row)"
            target="_blank"
            class="button is-primary is-success"
            style="margin-left: 10px;"
          >
            {{ $t('attributes.preview') }}
          </a>
        </div>
      </b-table-column>

      <template #top-left>
        <b-field>
          <span class="is-valid offer-top-desc">上架且未過效期</span>
          <span class="is-invalid offer-top-desc">上架但效期已過</span>
          <span class="is-off offer-top-desc">下架</span>
        </b-field>
      </template>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <news-form :news="selectedItem" @send="onSend" />
    </b-modal>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { NEWS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import NewsForm from './NewsForm'

export default {
  name: 'NewsList',
  components: { NewsForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    const { startDate, endDate } = this.calculateDateRange(this.query)
    const { startNewsDate, endNewsDate } = this.calculateNewsDateRange()

    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterStatus: this.query.status || '',
      filterType: this.query.type || '',
      filterCatalog: this.query.catalog || '',
      search: this.query.search || '',
      sortField: this.query.sort || 'create_time',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'desc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      selectedDates: [startDate, endDate],
      startDate,
      endDate,

      selectedNewsDates: [],
      startNewsDate,
      endNewsDate,

      isFormModalActive: false,
      sending: false,

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingAreas || this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${NEWS_API}`
        const params = {
          active: this.filterStatus && this.filterStatus === 'true',
          type: this.filterType || null,
          catalog: this.filterCatalog || null,
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate,
          start_news_date: this.startNewsDate,
          end_news_date: this.endNewsDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('news', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || ''
      this.filterType = query.type || ''
      this.filterCatalog = query.catalog || ''
      this.search = query.search || ''
      this.sortField = query.sort || 'create_time'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      const { startDate, endDate } = this.calculateDateRange(query)
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]
    },
    calculateDateRange(query) {
      let startDate = null
      let endDate = null

      if (query.start && query.end) {
        startDate = this.$dayjs.unix(query.start).toDate()
        endDate = this.$dayjs.unix(query.end).toDate()
      } else {
        const today = this.$dayjs()
        startDate = today.subtract(1, 'week').toDate()
        endDate = today.toDate()
      }

      return { startDate, endDate }
    },
    calculateNewsDateRange() {
      const today = this.$dayjs()
      let startDate = today.subtract(3, 'month').toDate()
      let endDate = today.add(3, 'month').toDate()

      return { startDate, endDate }
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        type: this.filterType || null,
        catalog: this.filterCatalog || null,
        search: this.search,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    async onSelectedNewsDates() {
      this.page = 1
      this.startNewsDate = this.selectedNewsDates[0]
      this.endNewsDate = this.selectedNewsDates[1]

      this.selectedNewsDates = [this.startNewsDate, this.endNewsDate]
      await this.loadItems()
    },
    onClearDates() {
      this.page = 1
      this.selectedDates = null
      this.startDate = null
      this.endDate = null

      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },
    setRowClass(row) {
      if (!row.status) {
        return 'is-off'
      } else {
        return this.checkOfferDateValid(row.start_date, row.end_date)
          ? 'is-valid'
          : 'is-invalid'
      }
    },
    checkOfferDateValid(start_date, end_date) {
      const nowDate = new Date()
      return (
        Date.parse(start_date) <= nowDate && Date.parse(end_date) >= nowDate
      )
    },
    onAdd() {
      this.selectedItem = {
        status: false,
        type: 'A',
        catalog: '近期活動'
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(NEWS_API, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${NEWS_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.NewsImage(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
